/**
 * Live chat toasts for office users — "Reece: vault is flooded" with an Open
 * button. Moved out of notification-bell.tsx (The Quiet Toolbar, 10/10) so the
 * toast keeps working when the bell is unmounted: it is live and short, which
 * is exactly what a toast is for.
 *
 * Exactly one mounted caller should have it enabled at a time, or one message
 * toasts twice: the bell enables it while it is mounted, and the top bar
 * enables it only when the quiet toolbar has taken the bell away.
 */
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSseEvent } from "@/lib/use-sse";

export function useChatToasts(enabled: boolean): void {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const onMessage = useCallback(
        (e: MessageEvent) => {
            try {
                const data = JSON.parse(e.data);
                const senderName = data.sender_name ?? "Someone";
                const body = data.body?.length > 60 ? data.body.slice(0, 60) + "..." : data.body;
                toast(`${senderName}: ${body}`, {
                    action: data.job_id
                        ? { label: "Open", onClick: () => navigate(`/field/chat?jobId=${data.job_id}`) }
                        : undefined,
                });
                qc.invalidateQueries({ queryKey: ["notifications"] });
                qc.invalidateQueries({ queryKey: ["chat", "unread-total"] });
            } catch {
                /* a malformed event is not worth a crash */
            }
        },
        [qc, navigate],
    );
    useSseEvent("chat:new_message", onMessage, enabled);
}
